import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { SEO } from "@/components/SEO";
import {
  Shield, ArrowRight, Home, Backpack, ClipboardList, FileText,
  QrCode, ArrowUpRight, Heart, CloudRain, Flame, Snowflake, Wind,
  AlertTriangle, Zap, ArrowLeft,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, EBtn } from "@/styles/editorial";

const FEATURES = [
  { icon: FileText, title: "Planes de emergencia", desc: "Guías de qué hacer antes, durante y después de cada tipo de emergencia.", c: C.red },
  { icon: Home, title: "Tarjeta de domicilio", desc: "Informa a los equipos de rescate sobre tus animales cuando no estés.", c: C.blue },
  { icon: Backpack, title: "Mochila de vida", desc: "Checklists para preparar todo: transportín, medicación, comida, agua...", c: C.orange },
  { icon: QrCode, title: "Código QR", desc: "QR con información de tus animales para acceso rápido en emergencias.", c: C.lilac },
];

const EMTYPES = [
  { id: "dana", label: "DANA", icon: CloudRain }, { id: "inundacion", label: "Inundación", icon: CloudRain },
  { id: "incendio", label: "Incendio", icon: Flame }, { id: "terremoto", label: "Terremoto", icon: AlertTriangle },
  { id: "huracan", label: "Huracán", icon: Wind }, { id: "nevada", label: "Nevada", icon: Snowflake },
];

export default function PreparaLanding() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const go = () => {
    if (user) navigate("/prepara/panel");
    else { localStorage.setItem("ea_pending_auth_role", "particular"); localStorage.setItem("ea_redirect_after_auth", "/prepara/panel"); navigate("/auth"); }
  };

  return (
    <div className="min-h-screen" data-testid="prepara-landing" style={{ background: C.white, fontFamily: BODY }}>
      <SEO
        title="Prepara"
        description="Herramientas de preparación ante emergencias con animales: planes de evacuación personalizados, Mochila de Vida, tarjeta de domicilio con QR y calendario de preparación."
        path="/prepara"
      />
      {/* Header */}
      <header className="sticky top-0 z-50 px-6 md:px-12 py-4"
        style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${C.navy}15` }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-semibold"
              style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}>
              <ArrowLeft className="w-4 h-4" /> Hub
            </button>
            <div className="h-5 w-px" style={{ background: `${C.navy}20` }} />
            <div className="flex items-center gap-2">
              <img src="/logos/logo-prepara.png" alt="Prepara" className="w-8 h-8 object-contain" />
              <div>
                <span className="text-sm font-bold tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>Prepara</span>
                <p className="text-[9px] tracking-[0.2em] font-medium uppercase" style={{ fontFamily: MONO, color: C.olive }}>Emergencia Animal</p>
              </div>
            </div>
          </div>
          <EBtn onClick={go} bg={C.navy} color={C.white} testId="prepara-header-access-btn">
            {user ? "Mi panel" : "Acceder"} <ArrowUpRight className="w-3.5 h-3.5" />
          </EBtn>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <Reveal>
              <EditorialLabel text="Prevenir es proteger" color={C.olive} />
              <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] leading-[0.95] tracking-tight mb-6" style={{ fontFamily: DISPLAY, color: C.navy }}>
                Prepárate<br />
                para proteger<br />
                <span style={{ color: C.orange }}>a tus animales</span>
              </h1>
              <p className="text-base md:text-lg max-w-xl mb-8 leading-relaxed" style={{ color: C.gray }}>
                Planes de emergencia personalizados, tarjetas de domicilio y checklists para tener todo listo.
                <strong style={{ color: C.navy }}> Porque la mejor emergencia es la que se previene.</strong>
              </p>
              <div className="flex flex-wrap gap-4">
                <EBtn onClick={go} bg={C.olive} color={C.white} testId="prepara-hero-start-btn">
                  {user ? "Ir a mi panel" : "Empezar ahora"} <ArrowRight className="w-4 h-4" />
                </EBtn>
                <EBtn onClick={() => navigate("/conecta")} outline bg={C.navy}>Ir a Conecta</EBtn>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5 flex justify-center md:justify-end">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="p-8" style={{ background: C.bg, borderRadius: 6, border: `1px solid ${C.navy}10` }}>
                  <img src="/logos/logo-prepara.png" alt="Prepara" className="w-40 h-40 md:w-48 md:h-48 object-contain mx-auto" />
                </div>
                <div className="absolute -top-2 -right-2 px-3 py-1.5 text-[10px] font-bold tracking-wider"
                  style={{ fontFamily: MONO, background: C.red, color: C.white, borderRadius: 4 }}>13 planes</div>
                <div className="absolute -bottom-2 -left-2 px-3 py-1.5 text-[10px] font-bold tracking-wider"
                  style={{ fontFamily: MONO, background: C.olive, color: C.white, borderRadius: 4 }}>Gratis</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 py-16 md:py-20" style={{ background: C.bg }}>
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <EditorialLabel number="01" text="Herramientas" color={C.olive} />
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.05] tracking-tight mb-12" style={{ fontFamily: DISPLAY, color: C.navy }}>
              Todo lo que <span style={{ color: C.olive }}>necesitas</span>
            </h2>
          </Reveal>
          <div className="space-y-0">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div className="grid grid-cols-12 gap-4 py-8 items-center" style={{ borderTop: `1px solid ${C.navy}10` }}>
                  <div className="col-span-1 hidden md:block">
                    <span className="text-xs font-semibold" style={{ fontFamily: MONO, color: C.lightgray }}>0{i + 1}</span>
                  </div>
                  <div className="col-span-12 md:col-span-1">
                    <f.icon className="w-6 h-6" style={{ color: f.c }} strokeWidth={1.5} />
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <h3 className="text-xl tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>{f.title}</h3>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="text-sm leading-relaxed" style={{ color: C.gray }}>{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div style={{ borderTop: `1px solid ${C.navy}10` }} />
          </div>
        </div>
      </section>

      {/* Emergency Types */}
      <section className="px-6 md:px-12 py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <EditorialLabel number="02" text="Tipos de emergencia" color={C.red} />
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.05] tracking-tight mb-10" style={{ fontFamily: DISPLAY, color: C.navy }}>
              Planes para cada <span style={{ color: C.red }}>emergencia</span>
            </h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {EMTYPES.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.04}>
                <div className="flex items-center gap-2 px-5 py-3 cursor-pointer transition-all hover:-translate-y-0.5"
                  style={{ background: C.white, border: `1px solid ${C.navy}10`, borderRadius: 4 }}>
                  <t.icon className="w-4 h-4" style={{ color: C.red }} strokeWidth={1.8} />
                  <span className="text-xs font-semibold tracking-wider" style={{ fontFamily: MONO, color: C.navy }}>{t.label}</span>
                </div>
              </Reveal>
            ))}
            <span className="flex items-center px-4 text-xs font-semibold" style={{ fontFamily: MONO, color: C.lightgray }}>+7 más</span>
          </div>
        </div>
      </section>

      {/* Domicile Card */}
      <section className="py-16 md:py-20" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <EditorialLabel number="03" text="Tarjeta de domicilio" color={C.orange} />
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.05] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.white }}>
              Protege a tus animales <span style={{ color: C.orange }}>cuando no estés</span>
            </h2>
            <p className="text-sm mb-10 leading-relaxed max-w-xl" style={{ color: C.lilac }}>
              Si hay una emergencia y no estás en casa, los equipos de rescate sabrán que hay animales que necesitan ayuda.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", icon: ClipboardList, title: "Registra tus animales", desc: "Nombre, especie, necesidades especiales..." },
              { step: "02", icon: FileText, title: "Genera tu tarjeta", desc: "Tarjeta visual con toda la info para tu puerta" },
              { step: "03", icon: QrCode, title: "Código QR incluido", desc: "QR con info actualizada y contactos de emergencia" },
            ].map((item, i) => (
              <Reveal key={item.step} delay={i * 0.1}>
                <div>
                  <span className="text-xs font-semibold tracking-wider" style={{ fontFamily: MONO, color: C.olive }}>Paso {item.step}</span>
                  <item.icon className="w-6 h-6 my-3" style={{ color: C.orange }} strokeWidth={1.5} />
                  <h3 className="text-lg tracking-tight mb-2" style={{ fontFamily: DISPLAY, color: C.white }}>{item.title}</h3>
                  <p className="text-sm" style={{ color: C.lilac }}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28" style={{ background: C.olive }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <Shield className="w-8 h-8 mb-5" style={{ color: C.white }} strokeWidth={1.5} />
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.white }}>
              La mejor emergencia<br />
              <span style={{ color: C.orange }}>es la que se previene</span>
            </h2>
            <p className="text-base mb-8 max-w-lg leading-relaxed" style={{ color: `${C.white}bb` }}>
              Empieza hoy a preparar tu plan de emergencia y protege a quienes más quieres.
            </p>
            <EBtn onClick={go} bg={C.white} color={C.navy} testId="prepara-cta-start-btn">
              {user ? "Ir a mi panel" : "Crear mi plan"} <ArrowRight className="w-4 h-4" />
            </EBtn>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
